/* ============================================================
   mazo-edge.js — drives the soft edge (CSS: mazo-edge.css §49; dials: mazo-tokens.css §1 "SOFT EDGE").

   It does one job: turn "how much content has scrolled under this edge" into a strength between 0 and 1 and write
   it on the element that owns the edge. The CSS paints; this only measures. Nothing is hard coded to a page:

     header (built in)         `--mazo-edge-page` on .header-wrapper
     [data-mazo-edge="bar"]    `--mazo-edge-bar`  on the bar     (strength from its next sibling's position)
     [data-mazo-edge="top"]    `data-mazo-edge-on` + `--mazo-edge-t` (px) on the scroller itself
     [data-mazo-edge="card"]   `--mazo-edge-card` + `--mazo-edge-pad` on the card (its fade colour is the card's own fill)
     [data-mazo-edge="page"]   no output of its own: its scrollTop counts as scroll under the header

   STRENGTH MEANS "CONTENT HAS ACTUALLY CROSSED THE EDGE", NOT "THE PAGE HAS SCROLLED". Each source subtracts the
   space that sits above its first content (a scroller's own top padding, the page's reserved header space), so a
   page resting at the top, a list not yet scrolled, or a bar whose cards have not reached it all read 0 and
   nothing is covered. The strength then ramps over RAMP x --mazo-edge-size px of travel, so the fade grows with
   the scroll instead of switching on. A pinned bar also drains as its last card leaves.

   WHY THE HEADER STEPS ASIDE FOR A PINNED BAR. The header strip would be painted over a pinned collection title
   (both live in the same 32px band under the header) and fade its text. While a bar is arriving, stuck or leaving,
   the header strip is scaled down by how much of the bar is under the header line; the bar brings its own fade.

   Written synchronously on scroll, never through requestAnimationFrame (the rAF-token deadlock, build-traps), and
   quantised to 1/50 so almost every scroll event is a no-op. Bound to Lenis as well as native scroll, the same way
   mazo-header.js does it, so a smooth-scrolled page and its fade move on the same frame.
   ============================================================ */
(function () {
  'use strict';

  var root = document.documentElement;
  var RAMP = 1;         // full strength once content has crossed RAMP x --mazo-edge-size px past the edge
  var LEAD = 8;         // px head start for a pinned bar's fade (see paint)
  var STEPS = 50;       // strength is written in 1/50 steps
  var size = 16;        // --mazo-edge-size, px (re-read on resize: the token steps down on tablet and phone)
  var barH = 50;        // --mazo-bar-h, px: the header's painted height, i.e. the line content slides under
  var restPad = 0;      // gap between the header's painted edge and the page's first content, at rest
  var header = null, bars = [], pages = [];
  var pads = new WeakMap();
  var lastY = -1;

  function clamp01(v) { return v < 0 ? 0 : v > 1 ? 1 : v; }
  function step(v) { return Math.round(v * STEPS) / STEPS; }
  function num(name) { return parseFloat(getComputedStyle(root).getPropertyValue(name)) || 0; }

  /* write a custom property only when it changed; the last value lives on the node, so a node that Dawn replaces
     (the cart list on every cart change) simply starts clean */
  function put(el, name, value) {
    var key = '_mzE' + name;
    if (el[key] === value) return;
    el[key] = value;
    el.style.setProperty(name, value);
  }

  /* the space above a scroller's first content: a card's padding, the PDP column's reserved header gap */
  function padTop(el) {
    var p = pads.get(el);
    if (p === undefined) { p = parseFloat(getComputedStyle(el).paddingTop) || 0; pads.set(el, p); }
    return p;
  }

  /* How far below the header's painted edge the page's first real content rests (a photo, a heading, a line of
     text). Measured, not assumed: each template reserves a different gap (the PDP adds section padding on top of
     the header space), and a wrong guess would start the fade early or late. Document coordinates, so it is right
     even if the page was restored part-way down. Skips anything hidden or inside a dialog. 0 when nothing is found. */
  function firstContentGap() {
    var main = document.getElementById('MainContent');
    if (!main) return 0;
    var list = main.querySelectorAll('h1,h2,h3,h4,p,li,img,video,button,a,summary,label');
    var y = window.scrollY || window.pageYOffset || 0;
    for (var i = 0; i < list.length; i++) {
      var el = list[i], r = el.getBoundingClientRect();
      if (r.width < 4 || r.height < 4) continue;
      if (el.closest('modal-dialog,dialog,[hidden],[aria-hidden="true"]')) continue;
      if (getComputedStyle(el).visibility === 'hidden') continue;
      return Math.max(0, r.top + y - barH);
    }
    return 0;
  }

  function refresh() {
    size = num('--mazo-edge-size') || 16;
    barH = num('--mazo-bar-h') || 50;
    restPad = firstContentGap();
    header = document.querySelector('.header-wrapper');
    bars = [].slice.call(document.querySelectorAll('[data-mazo-edge="bar"]'));
    pages = [].slice.call(document.querySelectorAll('[data-mazo-edge="page"]'));
    pads = new WeakMap();
  }

  /* The header strip and every pinned bar. Reads first, writes after, so one scroll event is one layout read. */
  function paint() {
    var ramp = size * RAMP;
    var under = Math.max(0, (window.scrollY || window.pageYOffset || 0) - restPad);
    for (var i = 0; i < pages.length; i++) under = Math.max(under, pages[i].scrollTop - padTop(pages[i]));
    var hdr = clamp01(under / ramp);

    var vh = window.innerHeight || 0, out = [];
    for (var j = 0; j < bars.length; j++) {
      var b = bars[j], r = b.getBoundingClientRect(), s = 0;
      if (r.bottom > 0 && r.top < vh) {
        var c = b.nextElementSibling;
        if (c) {
          var cr = c.getBoundingClientRect();
          /* LEAD: the fade starts this many px before the first card reaches the bar's lower edge (owner 2026-10-27: on a phone the blur
             came a little late once the gap under the heading was reduced) */
          s = clamp01((r.bottom - cr.top + LEAD) / ramp) * clamp01((cr.bottom - r.bottom) / ramp);
        }
        var near = clamp01(1 - (r.top - barH) / size);           // 0 far below the header line, 1 once it reaches it
        var vis = clamp01((r.bottom - barH) / (r.height || 1));  // share of the bar still below the header line
        hdr *= 1 - near * vis;
      }
      out.push(s);
    }
    for (var k = 0; k < bars.length; k++) put(bars[k], '--mazo-edge-bar', step(out[k]));
    if (header) put(header, '--mazo-edge-page', step(hdr));
  }

  /* A scroller that is shown, hidden or resized has a new scroll position without having fired a scroll event (a
     closed dialog resets to the top). One ResizeObserver per scroller, created on its first scroll, re-reads it. */
  var ro = window.ResizeObserver ? new ResizeObserver(function (entries) {
    for (var i = 0; i < entries.length; i++) scroller(entries[i].target);
  }) : null;

  /* One scroller moved (a cart list, a dialog card, the PDP column). */
  function scroller(el) {
    if (ro && !el._mzERo) { el._mzERo = 1; ro.observe(el); }
    var mode = el.getAttribute('data-mazo-edge');
    var s = clamp01((el.scrollTop - padTop(el)) / (size * RAMP));
    if (mode === 'top') {
      var on = s > 0;
      if (on !== el.hasAttribute('data-mazo-edge-on')) {
        if (on) el.setAttribute('data-mazo-edge-on', ''); else el.removeAttribute('data-mazo-edge-on');
      }
      if (on) put(el, '--mazo-edge-t', (Math.round(s * size * 2) / 2) + 'px');
    } else if (mode === 'card') {
      put(el, '--mazo-edge-pad', padTop(el) + 'px');   // see mazo-edge.css §49e: a sticky strip stops below the padding
      put(el, '--mazo-edge-card', step(s));
    } else if (mode === 'page') {
      paint();
    }
  }

  function onWindowScroll() {
    var y = window.scrollY || window.pageYOffset || 0;
    if (y === lastY) return;     // Lenis and the native event both report each move; the second is a no-op
    lastY = y;
    paint();
  }

  function settle() {
    refresh();
    lastY = -1;
    paint();
    [].forEach.call(document.querySelectorAll('[data-mazo-edge="top"],[data-mazo-edge="card"],[data-mazo-edge="page"]'), scroller);
  }

  /* `scroll` does not bubble, so a capture listener on the document sees every scroller, including ones that did
     not exist yet when this ran (the cart list is replaced by Dawn on each cart change; dialogs are built on open) */
  document.addEventListener('scroll', function (e) {
    var t = e.target;
    if (t && t.nodeType === 1 && t.hasAttribute('data-mazo-edge')) scroller(t);
  }, { capture: true, passive: true });

  window.addEventListener('scroll', onWindowScroll, { passive: true });
  window.addEventListener('resize', settle, { passive: true });
  window.addEventListener('load', settle);
  window.addEventListener('pageshow', settle);
  document.addEventListener('shopify:section:load', settle);

  var lenisBound = false;   // BIND TO LENIS AT MOST ONCE (same guard as mazo-header.js and mazo-footer.js)
  function bindLenis() {
    if (lenisBound || !(window.mazoLenis && window.mazoLenis.on)) return;
    lenisBound = true;
    window.mazoLenis.on('scroll', onWindowScroll);
  }
  bindLenis();
  window.addEventListener('mazo:lenis-ready', bindLenis);   // Lenis loads late since 2026-10-08

  settle();
})();
